export type CalendarMode = "day" | "month" | "quarter" | "half" | "year";
export type DateBoundary = "start" | "end";

/** Local calendar dates stay independent of UTC offsets and daylight saving. */
export function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function calendarDate(value: string): Date | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!parts) return null;
  const [, y, m, d] = parts;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return isoDate(date) === value ? date : null;
}

export function periodDate(year: number, month: number, months: number, boundary: DateBoundary): string {
  return isoDate(boundary === "start" ? new Date(year, month, 1) : new Date(year, month + months, 0));
}

/** Explicit dates or periods: 2027-05-20, 2027. 5. 20., 2027-05, 2027 Q2, 2027 H1, 2027. */
export function parseCalendarInput(text: string, boundary: DateBoundary): string | null {
  const value = text.trim();
  const day = /^(\d{4})\s*[-./년]\s*(\d{1,2})\s*[-./월]\s*(\d{1,2})\s*[.일]?$/.exec(value);
  if (day) {
    const date = calendarDate(`${day[1]}-${day[2]!.padStart(2, "0")}-${day[3]!.padStart(2, "0")}`);
    return date ? isoDate(date) : null;
  }
  const month = /^(\d{4})\s*[-./년]\s*(\d{1,2})\s*[.월]?$/.exec(value);
  if (month && Number(month[2]) >= 1 && Number(month[2]) <= 12) return periodDate(Number(month[1]), Number(month[2]) - 1, 1, boundary);
  const period = /^(\d{4})\s*([QH])\s*([1-4])$/i.exec(value);
  if (period) {
    const months = period[2]!.toUpperCase() === "Q" ? 3 : 6;
    const index = Number(period[3]) - 1;
    return index * months < 12 ? periodDate(Number(period[1]), index * months, months, boundary) : null;
  }
  return /^\d{4}$/.test(value) && Number(value) >= 1000 ? periodDate(Number(value), 0, 12, boundary) : null;
}

export function dateInRange(value: string, min?: string | null, max?: string | null): boolean {
  return !!calendarDate(value) && (!min || value >= min) && (!max || value <= max);
}
